<script lang="ts" setup>
import { MountingMaterial } from '@/models/MountingMaterial.ts'
import IconMountingType from '@/components/icons/IconMountingType.vue'
import RadioButton from '@/components/RadioButton.vue'
import FormElement from '@/components/FormElement.vue'
import { ref, watch } from 'vue'

const props = defineProps<{
  mountingMaterial: MountingMaterial
  modelValue: MountingMaterial
  disabled: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: MountingMaterial): void
}>()

const selected = ref<MountingMaterial>(props.modelValue)

watch(() => props.modelValue, value => {
  selected.value = value
}, { immediate: true })

function updateSelected(newSelected: MountingMaterial) {
  selected.value = newSelected
  emit('update:modelValue', selected.value)
}

</script>

<template>
  <div class="fTable__item is--select">
    <FormElement>
      <RadioButton
        :id="'mounting-material-' + mountingMaterial"
        :disabled="disabled"
        :model-value="selected"
        :value="mountingMaterial"
        name="mounting-material"
        @update:model-value="updateSelected"
      />

      <label :for="'mounting-material-' + mountingMaterial" class="fTable__tr fTable__cb">
        <div class="fTable__td column is--product">
          <IconMountingType :mounting-material="mountingMaterial" />

          <div class="fTable__product-infos">
            <h3 class="product__title">{{ $t('step_installation.' + mountingMaterial.toLowerCase() + '_title') }}</h3>
            <p>{{ $t('step_installation.' + mountingMaterial.toLowerCase() + '_description') }}</p>
          </div>
        </div>
      </label>
    </FormElement>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

form {
  section {
    padding: rem-calc(32);

    @include media('<tablet') {
      padding: 1rem;
    }

    &:not(:last-child) {
      border-bottom: 1px solid $borderColorLight;
    }

    .form__info {
      margin-bottom: 0;
      font-size: 0.875rem;
    }

    .section__headline {
      margin-bottom: 2rem;
    }

    .product__title {
      font-size: 1.25rem;
      margin-bottom: 0;
    }

    .has--selects {
      width: 100%;
      border: 1px solid $borderColor;

      @include themify($themes) {
        border-radius: rem-calc(themed('cardBorderRadius'));
      }
    }

    svg {
      margin-right: 2rem;
      width: 8rem;
    }

    .form__checkbox {
      display: flex;
    }

    .form__element {
      margin-bottom: 0;
      width: 100%;
    }

    p:last-of-type {
      margin-bottom: 0;
    }

    .fTable.is--products-table .fTable__td.is--product {
      padding-top: 1rem;
      padding-bottom: 1rem;
      padding-right: 2rem;
      flex-direction: row;
      align-items: center;
    }

    .fTable__item:has(input:disabled) {
      opacity: 0.3;
      cursor: not-allowed;

      * {
        cursor: not-allowed;
      }

      .form__info {
        color: $textColor;
      }
    }
  }
}
</style>
